<template>
  <main class="main">
    <div class="onboarding-wrap content-wrap">
      <!-- Крок 0: Старт -->
      <div v-if="currentStep === 0">
        <span class="eyebrow">Онбординг субагента</span>
        <h1 class="hero-title">Відкрийте доступ до страхових продуктів <em>UConnect</em></h1>
        <p class="page-sub">Заповніть дані один раз — ми самі сформуємо документи для реєстрації у страхових компаніях та передамо їх на підпис.</p>

        <div class="stats">
          <div class="stat">
            <div class="stat-num">{{ INSURERS.length }}</div>
            <div class="stat-label">страхових компаній</div>
          </div>
          <div class="stat">
            <div class="stat-num">~25</div>
            <div class="stat-label">хвилин на заповнення</div>
          </div>
          <div class="stat">
            <div class="stat-num">100%</div>
            <div class="stat-label">автоматична подача</div>
          </div>
        </div>

        <div class="grid grid-2" style="margin-top:20px; align-items:start;">
          <section class="card">
            <h3 class="card-title">🗺️ Маршрут онбордингу</h3>
            <div class="route">
              <template v-for="(s, i) in STEPS">
                <span :key="'r-'+i" class="route-item">{{s.icon}} {{ s.label }}</span>
                <span v-if="i < totalSteps - 1" :key="'a-'+i" class="route-arrow">→</span>
              </template>
            </div>
          </section>

          <section class="card">
            <h3 class="card-title">📎 Що варто підготувати заздалегідь</h3>
            <ul class="checklist">
              <li v-for="(t, i) in PREP_CHECKLIST" :key="'p-'+i">
                <span class="dot"></span>
                <span>{{ t }}</span>
              </li>
            </ul>
          </section>
        </div>

        <div class="assurances">
          <span class="assurance">✓ Автозбереження після кожного кроку</span>
          <span class="assurance">✓ Можна продовжити пізніше</span>
          <span class="assurance">✓ Захищене з’єднання</span>
        </div>
        <div style="margin-top:20px; align-items:start;">
          <button
            type="button"
            class="btn btn-primary btn-start"
            @click="selectStep(1)"
          >🚀 Розпочати онбординг</button>
        </div>
      </div>

      <!-- Крок 1: Персональна інформація -->
      <div v-if="currentStep === 1">
        <h2 class="page-title">Персональна інформація 👤</h2>
        <div class="stack">
          <section class="card">
            <h3 class="card-title">Прізвище, ім'я, по батькові</h3>
            <div class="grid grid-3">
              <label class="field" :class="{ 'has-error': hasError('form.last_name') }">
                <span class="field-label">Прізвище <span class="req">*</span></span>
                <input v-model="stateData.form.last_name" class="input" type="text" @blur="touchField('form.last_name')">
                <span v-if="hasError('form.last_name')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field" :class="{ 'has-error': hasError('form.first_name') }">
                <span class="field-label">Ім'я <span class="req">*</span></span>
                <input v-model="stateData.form.first_name" class="input" type="text" @blur="touchField('form.first_name')">
                <span v-if="hasError('form.first_name')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field">
                <span class="field-label">По батькові</span>
                <input v-model="stateData.form.middle_name" class="input" type="text">
              </label>
            </div>

            <div class="grid grid-2" style="margin-top:14px;">
              <label class="field" :class="{ 'has-error': hasError('form.birth_date') }">
                <span class="field-label">Дата народження <span class="req">*</span></span>
                <!-- 'form-control', 'form-input_date',  -->
                <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.birth_date"
                       placeholder="ДД.ММ.РРРР"
                       :class="['input']"
                       @blur="touchField('form.birth_date')">
                <!--                <date-picker v-model="stateData.form.birth_date" :config="optionsStartOrderDate" class="input" placeholder="ДД.ММ.РРРР"></date-picker>-->
                <!--                <input v-model="stateData.form.birth_date" class="input" placeholder="ДД.ММ.РРРР" type="text" @blur="touchField('form.birth_date')">-->
                <span v-if="hasError('form.birth_date')" class="field-error">Формат має бути ДД.ММ.РРРР</span>
              </label>

              <label class="field" :class="{ 'has-error': hasError('form.citizenship') }">
                <span class="field-label">Громадянство <span class="req">*</span></span>
                <input v-model="stateData.form.citizenship" class="input" placeholder="Україна" type="text" @blur="touchField('form.citizenship')">
                <span v-if="hasError('form.citizenship')" class="field-error">Обов'язкове поле</span>
              </label>

              <div class="field" :class="{ 'has-error': hasError('form.city_birth') }">
                <span class="field-label">Місце народження <span class="req">*</span></span>
                <multiselect
                  :value="cityBirthModel"
                  :options="citiesOptions"
                  :internal-search="false"
                  :options-limit="300"
                  :max-height="165"
                  :allow-empty="false"
                  :loading="citiesLoading"
                  track-by="name_full_name_ua"
                  label="name_full_name_ua"
                  placeholder="Наприклад: с. Косівка"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  @search-change="searchCities"
                  @input="setCityBirth"
                >
                  <span slot="noResult">Нічого не знайдено</span>
                  <span slot="noOptions">Почніть вводити назву</span>
                </multiselect>
                <span v-if="hasError('form.city_birth')" class="field-error">Обов'язкове поле</span>
              </div>

            </div>
          </section>

          <section class="card">
            <h3 class="card-title">РНОКПП</h3>
            <!-- @input="stateData.noRnokpp = !stateData.noRnokpp" -->
            <ToggleSwitch
              :class="['flex', 'align-center', ',ml-3', 'switch-row', { 'is-on': stateData.noRnokpp }]"
              v-model="stateData.noRnokpp"
            >
              Маю відмову від РНОКПП з релігійних переконань
            </ToggleSwitch>
            <p v-if="stateData.noRnokpp" class="field-hint">Відмітка про відмову має бути в паспорті.</p>
            <label v-else class="field" :class="{ 'has-error': hasError('form.rnokpp') }">
              <span class="field-label">РНОКПП <span class="req">*</span></span>
              <input
                v-model="stateData.form.rnokpp"
                class="input" maxlength="10"
                placeholder="10 цифр" type="text"
                inputmode="numeric"
                @input="formatInput('rnokpp', 'digits', 10)"
                @blur="touchField('form.rnokpp')"
              >
              <span v-if="hasError('form.rnokpp')" class="field-error">Введіть 10 цифр РНОКПП</span>
            </label>
          </section>

          <section class="card">
            <h3 class="card-title">Документ, що посвідчує особу</h3>
            <div class="chips">
              <button
                type="button"
                class="chip"
                :class="{ 'is-on': stateData.docType === 'passport' }"
                @click="stateData.docType = 'passport'"
              >🛂 Паспорт книжка</button>
              <button
                type="button"
                class="chip"
                :class="{ 'is-on': stateData.docType === 'id' }"
                @click="stateData.docType = 'id'"
              >🪪 ID-картка</button>
            </div>

            <div style="margin-top:14px;">
              <template v-if="stateData.docType === 'passport'">
                <div class="grid grid-2">
                  <label class="field" :class="{ 'has-error': hasError('form.passport_series') }">
                    <span class="field-label">Серія</span>
                    <input
                      v-model="stateData.form.passport_series"
                      class="input"
                      maxlength="2"
                      placeholder="СН"
                      type="text"
                      @input="formatInput('passport_series', 'ua_series', 2)"
                    >
                    <span v-if="hasError('form.passport_series')" class="field-error">Вкажіть серію паспорту</span>
                  </label>

                  <label class="field" :class="{ 'has-error': hasError('form.passport_number') }">
                    <span class="field-label">Номер документа <span class="req">*</span></span>
                    <input
                      v-model="stateData.form.passport_number"
                      class="input"
                      maxlength="6"
                      placeholder="6 цифр"
                      type="text"
                      inputmode="numeric"
                      @input="formatInput('passport_number', 'digits', 6)"
                      @blur="touchField('form.passport_number')"
                    >
                    <span v-if="hasError('form.passport_number')" class="field-error">Обов'язкове поле</span>
                  </label>
                </div>
                <div class="grid grid-2">
                  <label class="field" :class="{ 'has-error': hasError('form.id_series') }">
                    <span class="field-label">УНЗР <span class="req"> (за наявності)</span></span>
                    <input
                      v-mask="'########-#####'" inputmode="numeric"
                      v-model.trim="stateData.form.passport_unzr"
                      placeholder="РРРРММДД-XXXXX"
                      class="input"
                      type="text"
                      @blur="touchField('form.id_series')"
                    >
                    <span v-if="hasError('form.id_series')" class="field-error">Вкажіть УНЗР (формат РРРРММДД-XXXXX)</span>
                  </label>
                  <label class="field" :class="{ 'has-error': hasError('form.passport_issued') }">
                    <span class="field-label">Дата видачі <span class="req">*</span></span>
                    <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.passport_issued" placeholder="ДД.ММ.РРРР" class="input" @blur="touchField('form.passport_issued')">
                    <span v-if="hasError('form.passport_issued')" class="field-error">Вкажіть дату (ДД.ММ.РРРР)</span>
                  </label>
                </div>
                <div style="margin-top:14px;">
                  <label class="field" :class="{ 'has-error': hasError('form.passport_authority') }">
                    <span class="field-label">Ким виданий <span class="req">*</span></span>
                    <input v-model="stateData.form.passport_authority" class="input" type="text" @blur="touchField('form.passport_authority')">
                    <span v-if="hasError('form.passport_authority')" class="field-error">Обов'язкове поле</span>
                  </label>
                </div>
              </template>

              <template v-else>
                <div class="grid grid-2">
                  <label class="field" :class="{ 'has-error': hasError('form.id_series') }">
                    <span class="field-label">УНЗР <span class="req">*</span></span>
                    <input
                      v-mask="'########-#####'" inputmode="numeric"
                      v-model.trim="stateData.form.id_series"
                      placeholder="РРРРММДД-XXXXX"
                      class="input"
                      type="text"
                      @blur="touchField('form.id_series')"
                    >
                    <span v-if="hasError('form.id_series')" class="field-error">Вкажіть УНЗР (формат РРРРММДД-XXXXX)</span>
                  </label>

                  <label class="field" :class="{ 'has-error': hasError('form.id_number') }">
                    <span class="field-label">Номер ID-картки <span class="req">*</span></span>
                    <input
                      v-model="stateData.form.id_number"
                      class="input"
                      maxlength="9"
                      placeholder="9 цифр"
                      type="text"
                      inputmode="numeric"
                      @input="formatInput('id_number', 'digits', 9)"
                      @blur="touchField('form.id_number')"
                    >
                    <span v-if="hasError('form.id_number')" class="field-error">Обов'язкове поле</span>
                  </label>
                </div>

                <div class="grid grid-2" style="margin-top:14px;">
                  <label class="field" :class="{ 'has-error': hasError('form.id_issued') }">
                    <span class="field-label">Дата видачі <span class="req">*</span></span>
                    <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.id_issued" placeholder="ДД.ММ.РРРР" class="input" @blur="touchField('form.id_issued')">
                    <span v-if="hasError('form.id_issued')" class="field-error">Вкажіть дату (ДД.ММ.РРРР)</span>
                  </label>

                  <label class="field" :class="{ 'has-error': hasError('form.id_expires') }">
                    <span class="field-label">Дійсна до <span class="req">*</span></span>
                    <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.id_expires" placeholder="ДД.ММ.РРРР" class="input" @blur="touchField('form.id_expires')">
                    <span v-if="hasError('form.id_expires')" class="field-error">Вкажіть дату (ДД.ММ.РРРР)</span>
                  </label>
                </div>

                <div style="margin-top:14px;">
                  <label class="field" :class="{ 'has-error': hasError('form.id_authority') }">
                    <span class="field-label">Орган, що видав (код) <span class="req">*</span></span>
                    <input v-mask="'####'" v-model="stateData.form.id_authority" class="input" placeholder="4 цифри" type="text" @blur="touchField('form.id_authority')">
                    <span v-if="hasError('form.id_authority')" class="field-error">Обов'язкове поле</span>
                  </label>
                </div>
              </template>
            </div>
          </section>

          <section class="card">
            <h3 class="card-title">Адреса реєстрації</h3>
            <div class="grid grid-3">
              <label class="field">
                <span class="field-label">Індекс</span>
                <input v-model="stateData.form.zip" class="input" type="text">
              </label>

              <label class="field" :class="{ 'has-error': hasError('form.region') }">
                <span class="field-label">Область <span class="req">*</span></span>
                <input v-model="stateData.form.region" class="input" type="text" @blur="touchField('form.region')">
                <span v-if="hasError('form.region')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field">
                <span class="field-label">Район</span>
                <input v-model="stateData.form.district" class="input" type="text">
              </label>
            </div>

            <div class="grid grid-4" style="margin-top:14px;">
              <label class="field" :class="{ 'has-error': hasError('form.city') }">
                <span class="field-label">Населений пункт <span class="req">*</span></span>
                <input v-model="stateData.form.city" class="input" type="text" @blur="touchField('form.city')">
                <span v-if="hasError('form.city')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field">
                <span class="field-label">Вулиця</span>
                <input v-model="stateData.form.street" class="input" type="text">
              </label>

              <label class="field">
                <span class="field-label">Будинок</span>
                <input v-model="stateData.form.building" class="input" type="text">
              </label>

              <label class="field">
                <span class="field-label">Квартира</span>
                <input v-model="stateData.form.apartment" class="input" type="text">
              </label>
            </div>
          </section>

          <section class="card">
            <h3 class="card-title">Контакти</h3>
            <div class="grid grid-2">
              <label class="field" :class="{ 'has-error': hasError('form.phone') }">
                <span class="field-label">Основний телефон <span class="req">*</span></span>
                <!-- @input="clearError('phone')" -->
                <input type="text"
                       :class="['input']"
                       v-mask="'+38 (###) ###-##-##'" inputmode="tel"
                       v-model.trim="stateData.form.phone"

                       @blur="touchField('form.phone')"
                >
                <!--                <input v-model="stateData.form.phone" class="input" placeholder="+380" type="text" @blur="touchField('form.phone')">-->
                <span v-if="hasError('form.phone')" class="field-error">Некоректний номер телефону</span>
              </label>

              <label class="field">
                <span class="field-label">Додатковий телефон</span>
                <input v-model="stateData.form.phone_alt" class="input" type="text">
              </label>
            </div>

            <div class="grid grid-2" style="margin-top:14px;">
              <label class="field" :class="{ 'has-error': hasError('form.email') }">
                <span class="field-label">Email <span class="req">*</span></span>
                <input v-model="stateData.form.email" class="input" type="email" @blur="touchField('form.email')">
                <span v-if="hasError('form.email')" class="field-error">Вкажіть коректний Email</span>
              </label>
              <label class="field">
                <span class="field-label">Адреса вебсайту</span>
                <input v-model="stateData.form.website" class="input" type="text">
              </label>
            </div>
          </section>
        </div>
      </div>

      <!-- Крок 2: Дані ФОП -->
      <div v-if="currentStep === 2">
        <h2 class="page-title">Дані ФОП 🗂</h2>
        <div class="stack">
          <section class="card">
            <h3 class="card-title">Основні відомості</h3>
<!--            <div class="grid grid-2">-->
<!--              <label class="field" :class="{ 'has-error': hasError('form.kved') }">-->
<!--                <span class="field-label">Основний КВЕД <span class="req">*</span></span>-->
<!--                <input v-model="stateData.form.kved" class="input" placeholder="66.22" type="text" @blur="touchField('form.kved')">-->
<!--                <span v-if="hasError('form.kved')" class="field-error">Обов'язкове поле</span>-->
<!--              </label>-->

<!--              <label class="field" :class="{ 'has-error': hasError('form.fop_reg_date') }">-->
<!--                <span class="field-label">Дата реєстрації ФОП <span class="req">*</span></span>-->
<!--                <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.fop_reg_date"-->
<!--                       placeholder="ДД.ММ.РРРР"-->
<!--                       :class="['input']"-->
<!--                       @blur="touchField('form.fop_reg_date')">-->
<!--                &lt;!&ndash;                <input v-model="stateData.form.fop_reg_date" class="input" placeholder="ДД.ММ.РРРР" type="text" @blur="touchField('form.fop_reg_date')">&ndash;&gt;-->
<!--                <span v-if="hasError('form.fop_reg_date')" class="field-error">Вкажіть дату (ДД.ММ.РРРР)</span>-->
<!--              </label>-->
<!--            </div>-->

            <div style="margin-top:18px;">
              <span class="field-label">Наявний КВЕД 66.22? <span class="req">*</span></span>
              <p class="field-hint" style="margin:4px 0 8px;">Діяльність страхових агентів і брокерів — обов'язковий для роботи посередником</p>
              <div class="chips" :class="{ 'chip-group-error': hasError('hasKved') }">
                <button
                  type="button"
                  class="chip"
                  :class="{ 'is-on-safe': stateData.hasKved == true }"
                  @click="stateData.hasKved = true"
                >✓ Так, є в ЄДР</button>
                <button
                  type="button"
                  class="chip"
                  :class="{ 'is-on': stateData.hasKved == false }"
                  @click="stateData.hasKved = false"
                >✗ Немає</button>
              </div>
              <span v-if="hasError('hasKved')" class="field-error">Оберіть варіант</span>
              <p v-if="stateData.hasKved === false" class="field-hint" style="margin-top:8px; color:#b45309;">
                Потрібно додати КВЕД 66.22 через «Дію» до подачі документів.
              </p>
            </div>

            <div class="grid grid-2" style="margin-top:18px;">
              <label class="field" :class="{ 'has-error': hasError('form.edr_number') }">
                <span class="field-label">Номер запису в ЄДР<span class="req">*</span></span>
                <input v-model="stateData.form.edr_number" class="input" placeholder="" type="text" @blur="touchField('form.edr_number')">
                <span v-if="hasError('form.edr_number')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field" :class="{ 'has-error': hasError('form.edr_date') }">
                <span class="field-label">Дата запису в ЄДР <span class="req">*</span></span>
                <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.edr_date"
                       placeholder="ДД.ММ.РРРР"
                       :class="['input']"
                       @blur="touchField('form.edr_date')">
                <!--                <input v-model="stateData.form.fop_reg_date" class="input" placeholder="ДД.ММ.РРРР" type="text" @blur="touchField('form.fop_reg_date')">-->
                <span v-if="hasError('form.edr_date')" class="field-error">Вкажіть дату (ДД.ММ.РРРР)</span>
              </label>
            </div>

            <div style="margin-top:18px;">
              <span class="field-label">Система оподаткування <span class="req">*</span></span>
              <div style="margin-top:8px;">
                <div class="chips" :class="{ 'chip-group-error': hasError('taxSystem') }">
                  <button
                    type="button"
                    class="chip"
                    :class="{ 'is-on': stateData.taxSystem === 'simplified' }"
                    @click="stateData.taxSystem = 'simplified'"
                  >⚡ Спрощена</button>
                  <button
                    type="button"
                    class="chip"
                    :class="{ 'is-on': stateData.taxSystem === 'general' }"
                    @click="stateData.taxSystem = 'general'"
                  >📊 Загальна</button>
                </div>
                <span v-if="hasError('taxSystem')" class="field-error">Оберіть систему оподаткування</span>
              </div>
            </div>
          </section>

          <section class="card">
            <h3 class="card-title">Отримувач коштів і банківські реквізити</h3>
            <div class="grid grid-2">
              <label class="field" :class="{ 'has-error': hasError('form.payee_name') }">
                <span class="field-label">ПІБ отримувача (ФОП) <span class="req">*</span></span>
                <input v-model="stateData.form.payee_name" class="input" type="text" @blur="touchField('form.payee_name')">
                <span v-if="hasError('form.payee_name')" class="field-error">Обов'язкове поле</span>
              </label>


              <label class="field" :class="{ 'has-error': hasError('form.payee_ipn') }">
                <span class="field-label">ІПН отримувача <span class="req">*</span></span>
                <input
                  v-model="stateData.form.payee_ipn"
                  class="input"
                  maxlength="10"
                  type="text"
                  inputmode="numeric"
                  @input="formatInput('payee_ipn', 'digits', 10)"
                  @blur="touchField('form.payee_ipn')"
                >
                <span v-if="hasError('form.payee_ipn')" class="field-error">Вкажіть 10 цифр ІПН</span>
              </label>
            </div>
            <div class="grid grid-2" style="margin-top:14px;">
              <label class="field" :class="{ 'has-error': hasError('form.iban') }">
                <span class="field-label">IBAN <span class="req">*</span></span>
                <input
                  v-model="stateData.form.iban"
                  class="input"
                  maxlength="29"
                  placeholder="UA..."
                  type="text"
                  @input="formatInput('iban', 'iban', 29)"
                  @blur="touchField('form.iban')"
                >
                <span v-if="hasError('form.iban')" class="field-error">Некоректний IBAN (починається з UA + 27 цифр)</span>
              </label>

              <label class="field" :class="{ 'has-error': hasError('form.bank_name') }">
                <span class="field-label">Назва банку <span class="req">*</span></span>
                <input v-model="stateData.form.bank_name" class="input" type="text" @blur="touchField('form.bank_name')">
                <span v-if="hasError('form.bank_name')" class="field-error">Обов'язкове поле</span>
              </label>
            </div>
          </section>

          <section class="card">
            <h3 class="card-title">Реєстр страхових посередників НБУ</h3>
            <span class="field-label">Ви зареєстровані у Реєстрі страхових посередників? <span class="req">*</span></span>
            <div style="margin-top:8px; max-width:280px;">
              <div class="chips" :class="{ 'chip-group-error': hasError('inRegistry') }">
                <button
                  type="button"
                  class="chip"
                  :class="{ 'is-on': stateData.inRegistry === true }"
                  @click="stateData.inRegistry = true"
                >Так</button>
                <button
                  type="button"
                  class="chip"
                  :class="{ 'is-on': stateData.inRegistry === false }"
                  @click="stateData.inRegistry = false"
                >Ні</button>
              </div>
              <span v-if="hasError('inRegistry')" class="field-error">Оберіть варіант</span>
            </div>
            <div v-if="stateData.inRegistry === true" class="grid grid-2" style="margin-top:16px;">

              <label class="field" :class="{ 'has-error': hasError('form.registry_number') }">
                <span class="field-label">Номер у Реєстрі</span>
                <input v-model="stateData.form.registry_number" class="input" type="text">
                <span v-if="hasError('form.registry_number')" class="field-error">Обов'язкове поле</span>
              </label>
              <label class="field" :class="{ 'has-error': hasError('form.registry_id') }">
                <span class="field-label">ID в Реєстрі</span>
                <input v-model="stateData.form.registry_id" class="input" type="text">
                <span v-if="hasError('form.registry_id')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field flex align-self-end" :class="{ 'has-error': hasError('form.registry_date') }">
                <span class="field-label">Дата внесення</span>
                <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.registry_date"
                       placeholder="ДД.ММ.РРРР"
                       :class="['input']"
                       @blur="touchField('form.registry_date')">
                <!--                <input v-model="stateData.form.registry_date" class="input" type="text">-->
                <span v-if="hasError('form.registry_date')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field flex" :class="{ 'has-error': hasError('form.registry_by') }">
                <span class="field-label">Публічний реєстратор в НБУ (СК, яка перша Вас внесла в реєстр НБУ)</span>
                <input v-model="stateData.form.registry_by" class="input" type="text">
                <span v-if="hasError('form.registry_by')" class="field-error">Обов'язкове поле</span>
              </label>
            </div>
            <p v-else class="field-hint" style="margin-top:10px;">
              Ми підготуємо заяву на внесення до Реєстру разом з іншими документами.
            </p>
          </section>
        </div>
      </div>

      <!-- Крок 3: Комплаєнс -->
      <div v-if="currentStep === 3">
        <h2 class="page-title">Комплаєнс 🛡</h2>
        <p class="page-sub">Відповідайте чесно — ці питання потрібні для відповідності вимогам НБУ та фінансового моніторингу.</p>

        <div class="compliance-tabs">
          <button
            v-for="(t, i) in COMPLIANCE_TABS"
            :key="i"
            type="button"
            class="tab"
            :class="{
              'is-on': stateData.complianceTab === i,
              'has-tab-error': hasComplianceTabError(i)
            }"
            @click="stateData.complianceTab = i"
          >
            {{ isComplianceTabDone(i) ? '✓ ' : '' }}{{ t }}
          </button>
        </div>

        <div class="stack-sm">
          <div
            v-for="q in currentTabQuestions"
            :key="q.key"
            class="question"
            :class="{
              'is-safe': stateData.compliance[q.key] === q.safe,
              'has-error': hasError('compliance.' + q.key)
            }"
          >
            <div class="question-head">
              <span class="question-text">{{ q.q }} <span class="req">*</span></span>
              <span v-if="q.critical" class="badge-critical">Критично</span>
            </div>
            <div class="chips">
              <button
                type="button"
                class="chip"
                :class="{
                  'is-on-safe': stateData.compliance[q.key] == true && q.safe == true,
                  'is-on-danger': stateData.compliance[q.key] == true && q.safe != true
                }"
                @click="answerCompliance(q.key, true)"
              >✓ Так</button>
              <button
                type="button"
                class="chip"
                :class="{
                  'is-on-safe': stateData.compliance[q.key] == false && q.safe == false,
                  'is-on': stateData.compliance[q.key] == false && q.safe != false
                }"
                @click="answerCompliance(q.key, false)"
              >✗ Ні</button>
            </div>
            <span v-if="hasError('compliance.' + q.key)" class="field-error" style="margin-top:6px;">
              Дайте відповідь на запитання
            </span>
          </div>
        </div>
      </div>


      <!-- Крок 4: Структура діяльності -->
      <div v-if="currentStep === 4">
        <h2 class="page-title">Структура діяльності <span class="sc-interp">⚙️</span></h2>
        <p class="page-sub">Наявність працівників і керівників з реалізації визначає перелік потрібних довідок.</p>

        <div class="grid grid-2" style="align-items:start;">
          <!-- Наймані працівники перемикач -->
          <section class="card" :class="{ 'has-error': hasError('hasEmployees') }">
            <p style="font-size:14px; font-weight:600; margin:0 0 12px;">Маєте найманих працівників із реалізації страхування? <span class="req">*</span></p>
            <div class="chips">
              <button
                type="button"
                class="chip"
                :class="{ 'is-on': stateData.hasEmployees === true }"
                @click="setHasEmployees(true)"
              >✓ Так</button>
              <button
                type="button"
                class="chip"
                :class="{ 'is-on': stateData.hasEmployees === false }"
                @click="setHasEmployees(false)"
              >✗ Ні</button>
            </div>
            <span v-if="hasError('hasEmployees')" class="field-error">Оберіть варіант</span>
          </section>

          <!-- Керівник з реалізації -->
          <section class="card" :class="{ 'has-error': hasError('hasSalesHead') }">
            <p style="font-size:14px; font-weight:600; margin:0 0 12px;">Маєте керівника з реалізації (найманий, не ФОП)? <span class="req">*</span></p>
            <div class="chips">
              <button
                type="button"
                class="chip"
                :class="{ 'is-on': stateData.hasSalesHead === true }"
                @click="setHasSalesHead(true)"
              >✓ Так</button>
              <button
                type="button"
                class="chip"
                :class="{ 'is-on': stateData.hasSalesHead === false }"
                @click="setHasSalesHead(false)"
              >✗ Ні</button>
            </div>
            <span v-if="hasError('hasSalesHead')" class="field-error">Оберіть варіант</span>
          </section>
        </div>

        <div v-if="!needsPerson" class="banner banner-success" style="margin-top:14px;">
          Система автоматично підготує довідку про відсутність найманих працівників для відповідних СК.
        </div>

        <!-- ФОРМА 1: Керівник з реалізації -->
        <div v-if="hasSalesHead" style="margin-top:14px;">
          <section class="card">
            <h3 class="card-title">Дані керівника з реалізації</h3>
            <div class="grid grid-3">
              <label class="field" :class="{ 'has-error': hasError('form.sales_head.name') }">
                <span class="field-label">ПІБ <span class="req">*</span></span>
                <input v-model="stateData.form.sales_head.name" class="input" type="text" @blur="touchField('form.sales_head.name')">
                <span v-if="hasError('form.sales_head.name')" class="field-error">Обов'язкове поле</span>
              </label>

              <label class="field" :class="{ 'has-error': hasError('form.sales_head.rnokpp') }">
                <span class="field-label">РНОКПП <span class="req">*</span></span>
                <input
                  v-model="stateData.form.sales_head.rnokpp"
                  class="input" type="text" maxlength="10"
                  @input="formatInput('rnokpp', 'digits', 10, 'sales_head')"
                  @blur="touchField('form.sales_head.rnokpp')"
                >
                <span v-if="hasError('form.sales_head.rnokpp')" class="field-error">Вкажіть 10 цифр РНОКПП</span>
              </label>

              <label class="field">
                <span class="field-label">Дата народження</span>
                <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.sales_head.birth"
                       placeholder="ДД.ММ.РРРР" class="input" @blur="touchField('form.sales_head.birth')">
              </label>
            </div>

            <div class="grid grid-3" style="margin-top:14px;">
              <label class="field">
                <span class="field-label">Посада</span>
                <input v-model="stateData.form.sales_head.position" class="input" type="text">
              </label>
              <label class="field">
                <span class="field-label">Дата призначення</span>
                <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="stateData.form.sales_head.appointed" placeholder="ДД.ММ.РРРР" class="input">
              </label>
              <label class="field">
                <span class="field-label">Сертифікат кваліфікації</span>
                <input v-model="stateData.form.sales_head.certificate" class="input" type="text">
              </label>
            </div>
          </section>
        </div>

        <!-- ФОРМА 2: Наймані працівники -->
        <div v-if="hasEmployees" style="margin-top:14px;">
          <div v-for="(emp, idx) in stateData.form.employees" :key="idx" style="margin-bottom:14px;">
            <section class="card">
              <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
                <h3 class="card-title" style="margin:0;">Дані працівника #{{ idx + 1 }}</h3>
                <button
                  v-if="stateData.form.employees.length > 1"
                  type="button"
                  style="color: #ef4444; background: none; border: none; cursor: pointer; font-weight: 600; font-size: 13px;"
                  @click="removeEmployee(idx)"
                >
                  ✕ Видалити
                </button>
              </div>

              <div class="grid grid-3">
                <label class="field" :class="{ 'has-error': hasError(`form.employees.${idx}.name`) }">
                  <span class="field-label">ПІБ <span class="req">*</span></span>
                  <input v-model="emp.name" class="input" type="text" @blur="touchField(`form.employees.${idx}.name`)">
                  <span v-if="hasError(`form.employees.${idx}.name`)" class="field-error">Обов'язкове поле</span>
                </label>

                <label class="field" :class="{ 'has-error': hasError(`form.employees.${idx}.rnokpp`) }">
                  <span class="field-label">РНОКПП <span class="req">*</span></span>
                  <input
                    v-mask="'##########'"
                    v-model="emp.rnokpp" class="input" type="text" maxlength="10"
                    @blur="touchField(`form.employees.${idx}.rnokpp`)"
                  >
                  <span v-if="hasError(`form.employees.${idx}.rnokpp`)" class="field-error">Вкажіть 10 цифр РНОКПП</span>
                </label>

                <label class="field">
                  <span class="field-label">Дата народження</span>
                  <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="emp.birth"
                         placeholder="ДД.ММ.РРРР" class="input" @blur="touchField(`form.employees.${idx}.birth`)">
                </label>
              </div>

              <div class="grid grid-3" style="margin-top:14px;">
                <label class="field">
                  <span class="field-label">Посада</span>
                  <input v-model="emp.position" class="input" type="text">
                </label>
                <label class="field">
                  <span class="field-label">Дата призначення</span>
                  <input v-mask="'##.##.####'" inputmode="numeric" v-model.trim="emp.appointed" placeholder="ДД.ММ.РРРР" class="input">
                </label>
                <label class="field">
                  <span class="field-label">Сертифікат кваліфікації</span>
                  <input v-model="emp.certificate" class="input" type="text">
                </label>
              </div>
            </section>
          </div>

          <!-- додання ще одного працвіника -->
          <button
            type="button"
            style="display: block; width: 100%; padding: 12px; border: 2px dashed #cbd5e1; background: #f8fafc; border-radius: 8px; cursor: pointer; text-align: center; font-weight: 600; color: #2563eb;"
            @click="addEmployee"
          >
            + Додати ще одного працівника
          </button>
        </div>
      </div>


      <!-- Крок 5: Пакет документів -->
      <div v-if="currentStep === 5">
        <h2 class="page-title">Пакет документів</h2>
        <p class="page-sub">
          Персональний чекліст, зібраний під ваші відповіді. Формати PDF/JPG/PNG, до 10 МБ на файл.
          Завантажено {{ uploadedDocsCount }} з {{ DOCUMENTS.length }}.
        </p>


        <div class="stack-sm">
          <div
            v-for="d in filteredDocuments"
            :key="d.key"
            class="doc"
            :class="{
              'is-uploaded': !!stateData.uploaded[d.key],
              'has-error': hasError('uploaded.' + d.key)
            }"
          >
            <!-- Для паспорта вибиралка -->
            <div class="doc-body">
              <div class="doc-label">
                {{ d.label }} <span v-if="d.key !== 'qualification'" class="req" style="color:var(--orange, #f5923e);">*</span>
              </div>
              <div v-if="d.hint" class="doc-hint">{{ d.hint }}</div>

              <div
                v-if="isMultiPageDoc(d.key) && !stateData.uploaded[d.key] && !processingDocs[d.key]"
                class="doc-mode"
              >
                <el-radio-group v-model="uploadMode[d.key]" size="mini">
                  <el-radio-button label="photos">📷 Фото сторінок</el-radio-button>
                  <el-radio-button label="pdf">📄 Один PDF</el-radio-button>
                </el-radio-group>
              </div>
            </div>

            <!-- Статус -->
            <div class="doc-actions">

              <span class="pill" :class="{ 'is-done': !!stateData.uploaded[d.key] }">
                <i v-if="loadingDocs[d.key]" class="el-icon-loading"></i>
                <template v-else-if="processingDocs[d.key]">
                  <i class="el-icon-loading"></i> Обробляється…
                </template>
                <template v-else>
                  {{ stateData.uploaded[d.key] ? '✓ Завантажено' : 'Очікує' }}
                </template>
              </span>

              <div class="doc-upload-btn">

                <!-- 1. лоадер загрузку -->
                <el-button
                  v-if="(loadingDocs[d.key] || processingDocs[d.key]) && !stateData.uploaded[d.key]"
                  loading
                  disabled
                  size="small"
                  type="warning"
                  plain
                >
                  {{ processingDocs[d.key] ? 'Обробка...' : 'Завантаження...' }}
                </el-button>

<!--                <el-button-->
<!--                  v-else-if="!stateData.uploaded[d.key] && d.key === 'passport'"-->
<!--                  size="small" type="warning" plain-->
<!--                  @click="openPagesModal(d)"-->
<!--                >Завантажити</el-button>-->

                <el-button
                  v-else-if="!stateData.uploaded[d.key] && isMultiPageDoc(d.key) && uploadMode[d.key] === 'photos'"
                  size="small" type="warning" plain
                  @click="openPagesModal(d)"
                >Додати фото</el-button>


                <file-input
                  v-else-if="!stateData.uploaded[d.key]"
                  :value="null"
                  :title="'Завантажити'"
                  :disabled="loadingDocs[d.key]"
                  @files-selected="handleFileUpload($event, d.key, d)"
                />

                <div v-else class="uploaded-file-info">
                  <i class="el-icon-document icon-doc"></i>
                  <span class="file-name" :title="getFileName(d.key)">
                    {{ getFileName(d.key) }}
                  </span>
                  <el-button
                    type="text"
                    class="btn-delete"
                    :loading="loadingDocs[d.key]"
                    @click="handleFileRemove(d.key)"
                    title="Видалити"
                  >
                    <i v-if="!loadingDocs[d.key]" class="el-icon-close"></i>
                  </el-button>
                </div>

              </div>
            </div>
          </div>
        </div>


        <p v-if="hasError('uploaded')" class="field-error" style="margin-top:10px; font-size:14px;">
          Завантажте всі обов'язкові документи для переходу далі.
        </p>
      </div>

      <!-- Крок 6: Страхові компанії -->
      <div v-if="currentStep === 6">
        <h2 class="page-title">Страхові компанії</h2>
        <p class="page-sub">
          Усі компанії обрані за замовчуванням — приберіть ті, з якими не працюватимете.
          Обрано {{ activeInsurers.length }} з {{ INSURERS.length }}.
        </p>

        <div class="insurers">
          <button
            v-for="i in INSURERS"
            :key="i.code"
            type="button"
            class="insurer"
            :class="{ 'is-on': !stateData.insurersOff[i.code] }"
            @click="toggleInsurer(i.code)"
          >
            <span class="insurer-checkbox" :class="{ 'is-on': !stateData.insurersOff[i.code] }">✓</span>
            <span class="insurer-name">{{ i.name }}</span>
          </button>
        </div>
        <p v-if="hasError('insurers')" class="field-error" style="margin-top:14px; font-size:14px;">
          Оберіть принаймні одну страхову компанію для продовження.
        </p>
      </div>


      <div v-if="currentStep === 7">
        <h2 class="page-title">Ознайомлення з договором 📄</h2>
        <p class="page-sub">Субагентський договір укладається з ТОВ «ЮІНШУР» — єдиний договір для всіх страхових компаній.</p>

        <div class="stack">
          <section class="card">
            <div class="company">
              <div class="company-mark">Ю</div>
              <div>
                <div class="company-name">ТОВ «ЮІНШУР»</div>
                <div class="company-meta">ЄДРПОУ: 45581136</div>
                <div class="company-meta">Реєстр посередників: № 00004153</div>
              </div>
            </div>
            <div class="divider"></div>
            <div class="grid grid-2">
              <div><div class="kv-label">Номер договору</div><div class="kv-value"><p>ID: {{ getUserId() }}</p></div></div>
              <div><div class="kv-label">Дата початку</div><div class="kv-value">{{ currentDateFormatted }}</div></div>
              <div><div class="kv-label">Строк дії</div><div class="kv-value">1 рік (автопродовження)</div></div>
<!--              <div><div class="kv-label">Документів у пакеті</div><div class="kv-value">{{ totalDocsCount }}</div></div>-->
            </div>
            <div style="display:flex; gap:10px; margin-top:18px;">
              <button
                type="button"
                class="btn btn-ghost"
                style="flex:1;"
                @click="viewContract"
              >
                👁 Переглянути договір
              </button>
              <button
                type="button"
                class="btn btn-ghost"
                :disabled="isDownloadingContract"
                @click="downloadContract"
              >
                {{ isDownloadingContract ? '⏳ Завантаження...' : '⬇ PDF' }}
              </button>
            </div>
          </section>

          <section class="card" :class="['d-flex',{ 'has-error': hasError('contractRead') }]">
            <Checkbox v-model="stateData.contractRead" ><!-- @change="stateData.contractRead = !stateData.contractRead" -->
              <span style="display:block; font-size:14.5px; font-weight:700; color:var(--ink, #1e293b);">
                  Ознайомлений(а) з договором <span class="req">*</span>
                </span>
            </Checkbox>
            <span style="display:block; font-size:13px; color:var(--faint, #94a3b8); line-height:1.4; margin-top:2px;">
              Я ознайомився/ознайомилася з умовами договору доручення та переліком документів у пакеті
            </span>

            <span v-if="hasError('contractRead')" class="field-error" style="margin-top:8px;">
              Необхідно підтвердити ознайомлення з договором
            </span>
          </section>
        </div>
      </div>


      <!-- КРОК 8: ПЕРЕВИРКА ДАНИХ -->
      <div v-if="currentStep === 8">
        <h2 class="page-title">Перевірка даних ✅</h2>
        <section class="card">

          <!-- 1. ИДЕТ ВЫПОЛНЕНИЕ ЗАПРОСА: ПОКАЗЫВАЕМ verificationLines -->
          <div v-if="isGeneratingDocs" class="stack" style="margin-bottom: 24px;">
            <div v-for="(t, i) in verificationLines" :key="i" style="display:flex; align-items:center; gap:14px;">
              <span class="milestone-tick" style="background:#16a34a; color:#fff; width:32px; height:32px; font-size:15px; display:inline-flex; align-items:center; justify-content:center; border-radius:50%;">
                ✓
              </span>
              <span style="font-size:15px; font-weight:600; color:#16a34a;">{{ t }}</span>
            </div>
            <div style="text-align: center; margin-top: 16px; color: #64748b; font-size: 14px;">
              <i class="el-icon-loading" style="margin-right: 6px;"></i> Формуємо пакет документів, будь ласка зачекайте...
            </div>
          </div>

          <!-- 2. НАЧАЛЬНОЕ СОСТОЯНИЕ (ДОКУМЕНТЫ ЕЩЕ НЕ СФОРМИРОВАНЫ) -->
          <div v-else-if="!stateData.docsGenerated" style="text-align:center; padding: 20px 0;">
            <div style="font-size:44px; margin-bottom:12px;">📑</div>
            <h3 style="font-size:20px; font-weight:800; margin:0 0 10px;">Готові до формування пакету документів</h3>
            <p style="text-align:center; font-size:14px; color:var(--soft, #64748b); line-height:1.5; margin:0 0 24px;">
              Натисніть кнопку нижче, щоб сформувати реєстраційні анкети та документи для {{ activeInsurers.length }} страхових компаній.
            </p>
            <button
              type="button"
              class="btn btn-primary btn-block"
              :disabled="isGeneratingDocs"
              @click="renderDocuments"
              style="margin-top:16px;"
            >
              🚀 Сформувати Документи
            </button>
          </div>

          <!-- 3. ПОСЛЕ УСПЕШНОЙ ОТРАБОТКИ АПИ: СКРЫВАЕМ verificationLines, ПОКАЗЫВАЕМ ИТОГ -->
          <div v-else>
            <div style="text-align:center; margin:28px 0 8px; font-size:44px;">🎉</div>
            <h3 style="text-align:center; font-size:20px; font-weight:800; margin:0 0 10px;">Пакет документів сформовано!</h3>
            <p style="text-align:center; font-size:14px; color:var(--soft, #64748b); line-height:1.5; margin:0 0 24px;">
              Сформовано анкети та реєстраційні документи для {{ activeInsurers.length }} страхових компаній. Тепер потрібно завірити їх через USign.
            </p>

            <div class="stats">
              <div class="stat" style="background:#f4f7fc; border:none;">
                <div class="stat-num">{{ activeInsurers.length }}</div>
                <div class="stat-label">страхових компаній</div>
              </div>
              <div class="stat" style="background:#f4f7fc; border:none;">
                <div class="stat-num">{{ stateData.totalGeneratedDocsCount }}</div>
                <div class="stat-label">документів</div>
              </div>
            </div>

            <!-- Блок автоперехода через 5 секунд -->
            <div style="margin-top: 24px; padding: 16px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; text-align: center;">
              <p v-if="redirectCountdown" style="margin: 0 0 12px 0; font-size: 14px; color: #15803d; font-weight: 600;">
                ⏱️ Автоматичний перехід на крок підписання через <strong>{{ redirectCountdown }}</strong> сек...
              </p>
              <div class="gen-actions" style="display:flex; gap:12px; justify-content:center;">
                <button type="button" class="btn btn-primary" @click="goToNextStepNow">
                  Перейти до підписання зараз →
                </button>
                <button type="button" class="btn btn-ghost" @click="renderDocuments">
                  🔄 Сформувати повторно
                </button>
              </div>
            </div>
          </div>

        </section>
      </div>

      <!-- Крок 9: Підписання -->
      <div v-if="currentStep === 9">

        <div v-if="allSigned" class="done">
          <!--          <AsideMenuLogo route="prime" />-->
          <div class="done-trophy">🏆</div>
          <h2 class="done-title">Вітаємо!</h2>
          <h2 class="done-title">Онбординг завершено</h2>
          <p class="done-sub">Ви успішно пройшли всі {{ totalSteps }} етапів. Документи вже передаються до страхових компаній.</p>

          <section class="card">
            <h3 class="card-title">Завершені етапи</h3>
            <div class="stack-sm">
              <div v-for="(m, i) in MILESTONES" :key="i" class="milestone">
                <span class="milestone-tick">✓</span>
                <span>{{ m.icon }}</span>
                <span class="milestone-label">{{ m.label }}</span>
                <span class="milestone-tick" style="width:22px; height:22px; font-size:11px;">✓</span>
              </div>
            </div>
          </section>

          <div class="stats" style="margin-top:16px;">
            <div class="stat">
              <div class="stat-num">{{ activeInsurers.length }}</div>
              <div class="stat-label">страхових компаній</div>
            </div>
            <div class="stat">
              <div class="stat-num">{{ totalDocsCount }}</div>
              <div class="stat-label">документів подано</div>
            </div>
            <div class="stat">
              <div class="stat-num">1</div>
              <div class="stat-label">договір підписано</div>
            </div>
          </div>

          <div class="next-panel" style="margin-top:16px;">
            <div class="next-panel-title">Що далі?</div>
            <ol>
              <li>Страхові компанії розглядатимуть вашу реєстрацію. Загалом 3–10 робочих днів залежно від СК.</li>
              <li>Щойно СК підтвердить — їх продукти автоматично відкриються у вашому кабінеті.</li>
              <!--              <li>Ви отримаєте сповіщення на email про кожну зміну статусу.</li>-->
            </ol>
          </div>

          <button type="button" class="btn btn-primary btn-block" style="margin-top:16px;" @click="$router.push({name: `prime`});">Перейти до кабінету →</button>
          <!--          <button type="button" class="btn btn-ghost btn-block" style="margin-top:10px;">Переглянути статус реєстрації у СК</button>-->
        </div>

        <div v-else>
          <h2 class="page-title">Підписання анкет і договору</h2>
          <p class="page-sub">Кожен документ і кожна анкета підписуються окремо через КЕП.</p>

          <!-- Прогресс подписи -->
          <section class="card">
            <div class="signing-progress-head">
              <strong>{{ signingCounts.signed }}/{{ signingCounts.total }} підписано</strong>
              <span>
                {{ remainingSigningCount > 0 ? `Залишилось ${remainingSigningCount} — це займе ~${signingMinutesLeft} хв` : 'Все підписано' }}
              </span>
              <!--              <strong>{{ signingCounts.signed }}/{{ signingCounts.total }} підписано</strong>-->
            </div>
            <div class="signing-track">
              <div class="signing-fill" :style="{ width: signingProgressPct + '%' }"></div>
            </div>
          </section>

          <div v-if="loadingStep9" style="text-align:center; padding: 20px;">
            Завантаження документів...
          </div>

          <div v-else class="stack-sm" style="margin-top:16px;">

            <!-- 1. Договор -->
            <div v-if="finalStepData.contract" class="group" :class="{ 'is-done': !!finalStepData.contract.signed_path }">
              <div class="group-head" @click="toggleGroup('contract')">
                <span class="group-icon">{{ finalStepData.contract.signed_path ? '✓' : '📄' }}</span>
                <div style="flex:1; min-width: 0;">
                  <div class="group-title">Підписання договору</div>
                  <div class="group-sub">{{ finalStepData.contract.name_ua }}</div>
                </div>
                <span class="pill" :class="{ 'is-done': !!finalStepData.contract.signed_path }">
                  {{ finalStepData.contract.signed_path ? 'Підписано' : 'Очікує' }}
                </span>
                <span class="group-chevron">{{ stateData.openGroup === 'contract' ? '▲' : '▼' }}</span>
              </div>

              <div v-if="stateData.openGroup === 'contract'" class="group-body">
                <div class="sign-item">
                  <div class="sign-row">
                    <div class="sign-row-info">
                      <span class="sign-row-label">{{ finalStepData.contract.name_ua }}</span>
                      <div class="sign-row-actions">
                        <button type="button" class="btn-link" @click="viewFileByDocId(finalStepData.contract.id)">👁 Переглянути</button>
                        <button type="button" class="btn-link" @click="downloadFileByDocId(finalStepData.contract.id, finalStepData.contract.name_ua)">⬇ PDF</button>
                      </div>
                    </div>

                    <div
                      class="doc-item"
                      :class="{ 'is-selected': isDocSelected(finalStepData.contract.id) }"
                    >
                      <div class="doc-info">
                          <span v-if="isDocSelected(finalStepData.contract.id)" class="badge badge-selected">
                          ✓ Обрано для підпису
                        </span>
                      </div>
                    </div>

                    <div class="sign-row-status">
                      <span v-if="finalStepData.contract.signed_path" class="signed-mark"><span class="tick">✓</span> Підписано</span>
                      <!-- :disabled="signingDocId === finalStepData.contract.id"  -->
                      <button v-else
                              type="button"
                              class="btn-sign"
                              :disabled="isSigningProgress && targetDocToSign && targetDocToSign.id === finalStepData.contract.id"
                              @click.stop="signAction(finalStepData.contract)"
                      >
                        {{ (isSigningProgress && targetDocToSign && targetDocToSign.id === finalStepData.contract.id) ? 'Підписання...' : 'Підписати' }}
                      </button>
                    </div>
                  </div>

                  <!-- Подписанный договор -->
                  <div v-if="finalStepData.contract.signed_path" class="signed-file-subrow">
                    <span class="signed-file-label">🔏 Підписаний договір:</span>
                    <button type="button" class="btn-link" @click="viewFileByDocId(finalStepData.contract.id, true)">👁 Переглянути</button>
                    <button type="button" class="btn-link" @click="downloadFileByDocId(finalStepData.contract.id, 'signed_' + finalStepData.contract.name_ua, true)">⬇ PDF</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 2. Заверение загруженных агентом документов (Крок 5) -->
            <div class="group" :class="{ 'is-done': finalStepData.user_docs.length > 0 && finalStepData.user_docs.every(d => d.signed_path) }">
              <div class="group-head" @click="toggleGroup('docs')">
                <span class="group-icon">{{ finalStepData.user_docs.every(d => d.signed_path) ? '✓' : '📄' }}</span>
                <div style="flex:1; min-width: 0;">
                  <div class="group-title">Завірення документів</div>
                  <div class="group-sub">{{ finalStepData.user_docs.filter(d => d.signed_path).length }}/{{ finalStepData.user_docs.length }} завірено</div>
                </div>
                <span class="pill" :class="{ 'is-done': finalStepData.user_docs.every(d => d.signed_path) }">
                  {{ finalStepData.user_docs.filter(d => d.signed_path).length }}/{{ finalStepData.user_docs.length }}
                </span>
                <span class="group-chevron">{{ stateData.openGroup === 'docs' ? '▲' : '▼' }}</span>
              </div>

              <div v-if="stateData.openGroup === 'docs'" class="group-body">
                <div v-for="d in finalStepData.user_docs" :key="d.id" class="sign-item">
                  <div class="sign-row">
                    <div class="sign-row-info">
                      <span class="sign-row-label">{{ d.name_ua }}</span>
                      <div class="sign-row-actions">
                        <button type="button" class="btn-link" @click="viewFileByDocId(d.id)">👁 Переглянути</button>
                        <button type="button" class="btn-link" @click="downloadFileByDocId(d.id, d.name_ua)">⬇ Файл</button>
                      </div>
                    </div>

                    <div class="doc-item" :class="{ 'is-selected': isDocSelected(d.id) }">
                      <div class="doc-info">
                        <span v-if="isDocSelected(d.id)" class="badge badge-selected">
                          ✓ Обрано для підпису
                        </span>
                      </div>
                    </div>

                    <div class="sign-row-status">
                      <span v-if="d.signed_path" class="signed-mark"><span class="tick">✓</span> Підписано</span>
                      <!-- :disabled="signingDocId === d.id"  -->
                      <button
                        v-else type="button"
                        class="btn-sign"
                        :disabled="isSigningProgress && targetDocToSign && targetDocToSign.id === d.id"
                        @click.stop="openSignModal(d)"
                      >
                        <!--                        {{ signingDocId === d.id ? 'Підписання...' : 'Підписати' }}-->
                        {{ (isSigningProgress && targetDocToSign && targetDocToSign.id === d.id) ? 'Підписання...' : 'Підписати' }}
                      </button>
                    </div>
                  </div>

                  <!-- Подписанная версия документа -->
                  <div v-if="d.signed_path" class="signed-file-subrow">
                    <span class="signed-file-label">🔏 Підписаний документ:</span>
                    <button type="button" class="btn-link" @click="viewFileByDocId(d.id, true)">👁 Переглянути</button>
                    <button type="button" class="btn-link" @click="downloadFileByDocId(d.id, 'signed_' + d.name_ua, true)">⬇ Файл</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Подписание сгенерированных анкет (28 файлов) -->
            <div class="group" :class="{ 'is-done': finalStepData.generated.length > 0 && finalStepData.generated.every(f => f.signed_path) }">
              <div class="group-head" @click="toggleGroup('generated')">
                <span class="group-icon">{{ finalStepData.generated.every(f => f.signed_path) ? '✓' : '📄' }}</span>
                <div style="flex:1; min-width: 0;">
                  <div class="group-title">Підписання анкет</div>
                  <div class="group-sub">{{ finalStepData.generated.filter(f => f.signed_path).length }}/{{ finalStepData.generated.length }} підписано</div>
                </div>
                <span class="pill" :class="{ 'is-done': finalStepData.generated.every(f => f.signed_path) }">
                  {{ finalStepData.generated.filter(f => f.signed_path).length }}/{{ finalStepData.generated.length }}
                </span>
                <span class="group-chevron">{{ stateData.openGroup === 'generated' ? '▲' : '▼' }}</span>
              </div>

              <div v-if="stateData.openGroup === 'generated'" class="group-body">
                <div v-for="f in finalStepData.generated" :key="f.id" class="sign-item">
                  <div class="sign-row">
                    <div class="sign-row-info">
                      <span class="sign-row-label">{{ f.name_ua }}</span>
                      <div class="sign-row-actions">
                        <button type="button" class="btn-link" @click="viewFileByDocId(f.id)">👁 Переглянути</button>
                        <button type="button" class="btn-link" @click="downloadFileByDocId(f.id, f.name_ua)">⬇ Файл</button>
                      </div>
                    </div>

                    <!-- isDocSelected(`generated_${f.id}`) -->
                    <div class="doc-item" :class="{ 'is-selected': isDocSelected(f.id) }">
                      <div class="doc-info">
                        <!--                        <span>{{ f.title || f.name }}</span>-->
                        <!-- Бейдж/Значок "Обрано" если документ выделен -->
                        <span v-if="isDocSelected(f.id)" class="badge badge-selected">
                          ✓ Обрано для підпису
                        </span>
                        <!--                        <span v-else-if="f.signed_path" class="badge badge-signed">-->
                        <!--                          ✓ Підписано-->
                        <!--                        </span>-->
                      </div>
                    </div>

                    <div class="sign-row-status">
                      <span v-if="f.signed_path" class="signed-mark"><span class="tick">✓</span> Підписано</span>
                      <button v-else type="button" class="btn-sign" :disabled="signingDocId === f.id" @click.stop="openSignModal(f)">
                        <!--                        {{ signingDocId === f.id ? 'Підписання...' : 'Підписати' }}-->
                        {{ (isSigningProgress && targetDocToSign && targetDocToSign.id === d.id) ? 'Підписання...' : 'Підписати' }}
                      </button>
                    </div>
                  </div>

                  <!-- Подписанная версия анкеты -->
                  <div v-if="f.signed_path" class="signed-file-subrow">
                    <span class="signed-file-label">🔏 Підписана анкета:</span>
                    <button type="button" class="btn-link" @click="viewFileByDocId(f.id, true)">👁 Переглянути</button>
                    <button type="button" class="btn-link" @click="downloadFileByDocId(f.id, 'signed_' + f.name_ua, true)">⬇ Файл</button>
                  </div>
                </div>
              </div>
            </div>



            <div v-if="allUnsignedDocs.length > 0" class="batch-sign-card mt-4 p-4 rounded-3 border">
              <!-- 1. Чекбокс "Выделить все" -->
              <div class="batch-sign-header mb-3 pb-3 border-bottom">
                <Checkbox v-model="selectAll" @change="toggleSelectAll">
                  <span class="group-title fw-semibold text-dark cursor-pointer">
                    Виділити всі непідписані документи ({{ allUnsignedDocs.length }})
                  </span>
                </Checkbox>
              </div>

              <!-- 2. Предупреждающая галочка (внутри transition) -->
              <transition name="fade">
                <div
                  v-if="selectAll || selectedDocIds.length > 0"
                  class="confirmation-alert-box p-3 mb-3 rounded-2 d-flex align-items-center"
                >
                  <Checkbox v-model="confirmSignAll">
                    <span class="group-title text-dark fs-14 cursor-pointer">
                      Я підтверджую, що ознайомлений(а) та бажаю підписати всі обрані документи (<strong>{{ selectedDocIds.length }}</strong>)
                    </span>
                  </Checkbox>
                </div>
              </transition>

              <!-- 3. Действие: Кнопка и инфо -->
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-3 pt-2">
                <div class="text-muted fs-14">
                  Обрано для підпису: <strong class="text-primary">{{ selectedDocIds.length }}</strong> з {{ allUnsignedDocs.length }}
                </div>

                <button
                  type="button"
                  class="btn btn-primary d-inline-flex align-items-center justify-content-center"
                  :disabled="!confirmSignAll || selectedDocIds.length === 0 || isBatchSigning"
                  @click="openBatchSignModal"
                >
                  <span v-if="isBatchSigning" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  <span>Підписати всі ({{ selectedDocIds.length }})</span>
                </button>
              </div>

            </div>

          </div>
        </div>
      </div>


      <PassportPagesModal
        v-if="pagesDoc"
        :visible.sync="showPagesModal"
        :doc-key="pagesDoc.key"
        :doc-label="pagesDoc.label"
        @confirmed="onPagesConfirmed"
      />

      <SignModal
        :visible.sync="showKepModal"
        :signing-mode="signingMode"
        :target-doc-to-sign="targetDocToSign"
        :selected-doc-count="selectedDocIds.length"
        :loading="isSigningProgress"
        @submit="onKepModalSubmit"
      />
    </div>


    <footer v-if="!(currentStep === 9 && showFooter)" class="onboarding-footer">
      <span class="footer-note">Прогрес збережено автоматично</span>
      <div v-if="currentStep !== 0" class="footer-actions">
        <button
          type="button"
          class="btn btn-ghost"
          :disabled="currentStep === 0 || isGeneratingDocs"
          @click="prevStep"
        >← Назад</button>

        <button
          type="button"
          class="btn btn-primary"
          :disabled="isNextDisabled"
          @click="nextStep"
        >{{ currentStep === totalSteps - 1 ? 'Завершити' : 'Далі →' }}</button>
      </div>
    </footer>
  </main>
</template>

<script>
import FileDownload from 'js-file-download';
import Multiselect from 'vue-multiselect';
import { STEPS, INSURERS, DOCUMENTS, ONBOARDING_TEMPLATES, COMPLIANCE_TABS, COMPLIANCE, PREP_CHECKLIST, MILESTONES } from '../data/onboardingData';
import FileInput from "@/components/app/onboarding/FileInput.vue";
import Checkbox from "@/components/app/onboarding/CustomCheckbox";
import { ToggleSwitch } from '@/shared/ui/form';
import SignModal from '@/components/app/onboarding/SignModal.vue';
import PassportPagesModal from '@/components/app/onboarding/PassportPagesModal.vue';
import { required, minLength, email, helpers, requiredIf } from 'vuelidate/lib/validators';
import {
  generateDocuments,
  showExampleDocument,
  getListDocuments,
  showDocumentFile,
  signDocument,
  signAllDocuments,
  completeOnboarding,
  checkOnboardingDocumentPagesStatus
} from '@/api/onboarding';
import {userMixin} from "@/mixin";
import profileForm from "@/components/app/profile/profileInfoForms/mixin/profileForm";

const isDateFormat = helpers.regex('isDate', /^\d{2}\.\d{2}\.\d{4}$/);
const isRnokpp = helpers.regex('isRnokpp', /^\d{10}$/);
const isKvedYes = (vm) => vm.stateData?.hasKved === true;
const isInRegistryYes = (vm) => vm.stateData?.inRegistry === true;
// const isPhone = helpers.regex('isPhone', /^\+?\d{10,12}$/);
const isPhone = (value) => {
  if (!value) return true;
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly.length === 12; // >= 10 && digitsOnly.length <= 12;
};
const isIban = helpers.regex('isIban', /^UA\d{27}$/i);
const isTrue = (val) => val === true;
const isSelected = (val) => val !== null && val !== undefined && val !== '';

const STORE_KEY = 'uconnect-onboarding-vue';
export default {
  name: "OnboardingMain",
  components: {
    FileInput, ToggleSwitch, Checkbox, SignModal, Multiselect, PassportPagesModal
  },
  mixins: [userMixin, profileForm],
  props: {
    stateData: { type: Object, default: () => ({
        form: {
          iban: 'UA',
        }
      }), },
    currentStep: { type: Number, required: true },
    totalSteps: { type: Number, required: true },
    activeInsurers: { type: Array, required: true, },
    showFooter: { type: Boolean, default: true },
    isGeneratingDocs: { type: Boolean, default: false },
  },
  data() {
    return {
      STEPS,
      INSURERS,
      DOCUMENTS,
      ONBOARDING_TEMPLATES,
      COMPLIANCE_TABS,
      COMPLIANCE,
      PREP_CHECKLIST,
      MILESTONES,
      optionsStartOrderDate: {
        format: 'DD.MM.YYYY',
        useCurrent: false,
        minDate: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()),
        locale: 'uk',
        icons: {
          previous: 'icon arrow-point-to-left',
          next: 'icon arrow-point-to-right',
        },
      },
      citiesOptions: [ { 'id': 1,'name': 'Київ','name_full_name_ua': 'м. Київ, Україна', 'zone':1 },
        { 'id': 9,'name': 'Дніпро','name_full_name_ua': 'м. Дніпро, Дніпропетровська  обл., Україна', 'zone':2 },
        { 'id': 11,'name': 'Львів','name_full_name_ua': 'м. Львів, Львівська  обл., Україна', 'zone':1 },
        { 'id': 13,'name': 'Одеса','name_full_name_ua': 'м. Одеса, Одеська  обл., Україна', 'zone':2 },
        { 'id': 14,'name': 'Харків','name_full_name_ua': 'м. Харків, Харківська  обл., Україна', 'zone':2 },
        { 'id': 16,'name': 'Запоріжжя','name_full_name_ua': 'м. Запоріжжя, Запорізька  обл., Україна', 'zone':3 },
        { 'id': 17,'name': 'Кривий Ріг','name_full_name_ua': 'м. Кривий Ріг, Дніпропетровська  обл., Україна', 'zone':3 },
        { 'id': 21,'name': 'Вінниця','name_full_name_ua': 'м. Вінниця, Вінницька  обл., Україна', 'zone':4 },
        { 'id': 36,'name': 'Маріуполь','name_full_name_ua': 'м. Маріуполь, Донецька  обл., Україна', 'zone':4 },
        { 'id': 41,'name': 'Полтава','name_full_name_ua': 'м. Полтава, Полтавська  обл., Україна', 'zone':4 }
      ],
      citiesLoading: false,
      // documents PassportModal
      showPagesModal: false,
      pagesDoc: null,          // { key, label }
      processingDocs: {},
      pollTimers: {},
      // for passport ways
      MULTI_PAGE_DOCS: ['passport'],
      uploadMode: { passport: 'photos' },

      loadingDocs: {},
      redirectCountdown: 5,
      redirectTimer: null,
      isDownloadingContract: false,
      loadingStep9: false,

      finalStepData: {
        contract: null,  // объект OnboardingDocument
        user_docs: [],  // массив OnboardingDocument (Крок 5)
        generated: [],      // массив OnboardingDocument
      },
      selectAll: false,          // set all
      confirmSignAll: false,
      selectedDocIds: [],
      isBatchSigning: false,
      signingDocId: null,

      // sign
      showKepModal: false,
      signingMode: 'single', // 'single' | 'batch'
      targetDocToSign: null,
      isSigningProgress: false,

      isCompleting: false,
      isOnboardingDone: false,
    }
  },
  validations() {
    // ------------------- КРОК 1 -------------------
    const step1FormRules = {
      last_name: { required },
      first_name: { required },
      birth_date: { required, isDateFormat },
      citizenship: { required },
      city_birth: { required },
      region: { required },
      city: { required },
      phone: { required, isPhone },
      email: { required, email }
    };

    if (!this.stateData.noRnokpp) {
      step1FormRules.rnokpp = { required, maxLength: 10, isRnokpp };
    }

    if (this.stateData.docType === 'passport') {
      step1FormRules.passport_series = { required };
      step1FormRules.passport_number = { required };
      step1FormRules.passport_issued = { required, isDateFormat };
      step1FormRules.passport_authority = { required };
    } else {
      step1FormRules.id_series = { required };
      step1FormRules.id_number = { required };
      step1FormRules.id_issued = { required, isDateFormat };
      step1FormRules.id_expires = { required, isDateFormat };
      step1FormRules.id_authority = { required };
    }

    const step1Rules = {
      stateData: {
        form: step1FormRules
      }
    };

    // ------------------- КРОК 2 -------------------
    const step2Rules = {
      stateData: {
        hasKved: { required, isSelected },
        taxSystem: { required, isSelected },
        inRegistry: { required, isSelected },
        form: {
          // kved: { required },
          // fop_reg_date: { required, isDateFormat },
          payee_name: { required },
          payee_ipn: { required, isRnokpp },
          iban: { required, isIban },
          bank_name: { required },

          edr_number: {
            required: requiredIf(function () {
              return this.stateData?.hasKved === true;
            }),
          },
          edr_date: {
            required: requiredIf(function () {
              return this.stateData?.hasKved === true;
            }),
            isDateFormat: requiredIf(function () {
              return this.stateData?.hasKved === true;
            }),
          },

          registry_number: {
            required: requiredIf(function () {
              return this.stateData?.inRegistry === true;
            }),
          },
          registry_id: {
            required: requiredIf(function () {
              return this.stateData?.inRegistry === true;
            }),
            // required: requiredIf(isInRegistryYes)
          },
          registry_date: {
            required: requiredIf(function () {
              return this.stateData?.inRegistry === true;
            }),
            isDateFormat: requiredIf(function () {
              return this.stateData?.inRegistry === true;
            }),
          },
          registry_by: {
            required: requiredIf(function () {
              return this.stateData?.inRegistry === true;
            }),
          }
        }
      }
    };

    // ------------------- КРОК 3 -------------------
    const complianceRules = {};
    this.COMPLIANCE.forEach(q => {
      complianceRules[q.key] = { required, isSelected };
    });
    const step3Rules = {
      stateData: {
        compliance: complianceRules
      }
    };

    // ------------------- КРОК 4 -------------------
    const step4FormRules = {};

    if (this.hasSalesHead) {
      step4FormRules.sales_head = {
        name: { required },
        rnokpp: { required, isRnokpp }
      };
    }

    if (this.hasEmployees) {
      step4FormRules.employees = {
        required,
        $each: {
          name: { required },
          rnokpp: { required, isRnokpp }
        }
      };
    }

    const step4Rules = {
      stateData: {
        hasEmployees: { required, isSelected },
        hasSalesHead: { required, isSelected },
        form: step4FormRules
      }
    };

    // ------------------- КРОК 5 -------------------
    const uploadedRules = {};
    const docsToValidate = this.stateData.noRnokpp
      ? this.DOCUMENTS.filter(d => d.key !== 'ipn')
      : this.DOCUMENTS;

    docsToValidate.forEach(d => {
      if (d.key !== 'qualification') {
        uploadedRules[d.key] = { required };
      }
    });

    const step5Rules = {
      stateData: {
        uploaded: uploadedRules
      }
    };

    // ------------------- КРОК 6 -------------------
    const step6Rules = {
      activeInsurers: {
        atLeastOne: (val) => val && val.length > 0
      }
    };

    // ------------------- КРОК 7 -------------------
    const step7Rules = {
      stateData: {
        contractRead: { isTrue }
      }
    };

    if (this.currentStep === 1) return step1Rules;
    if (this.currentStep === 2) return step2Rules;
    if (this.currentStep === 3) return step3Rules;
    if (this.currentStep === 4) return step4Rules;
    if (this.currentStep === 5) return step5Rules;
    if (this.currentStep === 6) return step6Rules;
    if (this.currentStep === 7) return step7Rules;

    return {};
  },
  computed: {
    cityBirthModel() {
      const v = this.stateData.form.city_birth;
      return v ? { name_full_name_ua: v } : null;
    },
    isNextDisabled() {
      if (this.isGeneratingDocs) return true;
      if (this.currentStep === 8 && !this.stateData.docsGenerated) return true;
      if (Object.values(this.processingDocs).some(Boolean)) return true;
      return false;
    },
    isLastStep() {
      return this.currentStep === this.totalSteps - 1;
    },
    currentTabQuestions() {
      return this.COMPLIANCE.filter(q => q.tab === this.stateData.complianceTab);
    },
    needsPerson() {
      return this.stateData.hasEmployees === true || this.stateData.hasSalesHead === true;
    },
    hasSalesHead() {
      return this.stateData.hasSalesHead === true;
    },
    hasEmployees() {
      return this.stateData.hasEmployees === true;
    },
    // uploadedDocsCount() {
    //   return this.DOCUMENTS.filter(d => this.stateData.uploaded[d.key]).length;
    // },
    filteredDocuments() {
      if (this.stateData.noRnokpp) {
        return this.DOCUMENTS.filter(d => d.key !== 'ipn');
      }
      return this.DOCUMENTS;
    },
    uploadedDocsCount() {
      return this.filteredDocuments.filter(d => !!this.stateData.uploaded[d.key]).length;
    },
    currentDateFormatted() {
      return new Date().toLocaleDateString('uk-UA');
    },
    verificationLines() {
      return [
        'Перевіряємо дані...',
        `Формуємо документи для ${this.activeInsurers.length} СК...`,
        'Готуємо пакет до підписання...',
        'Пакет документів сформовано!',
      ];
    },
    signedDocsCount() {
      return this.DOCUMENTS.filter(d => this.stateData.signedDocs[d.key]).length;
    },
    signedFormsCount() {
      return this.activeInsurers.filter(i => this.stateData.signedForms[i.code]).length;
    },
    allSigned() {
      const c = this.signingCounts;
      return c.total > 0 && c.signed === c.total;
    },
    signingCounts() {
      let signed = 0;
      let total = 0;

      if (this.finalStepData?.contract) {
        total += 1;
        if (this.finalStepData.contract.signed_path) {
          signed += 1;
        }
      }

      if (Array.isArray(this.finalStepData?.user_docs)) {
        total += this.finalStepData.user_docs.length;
        signed += this.finalStepData.user_docs.filter(d => !!d.signed_path).length;
      }

      if (Array.isArray(this.finalStepData?.generated)) {
        total += this.finalStepData.generated.length;
        signed += this.finalStepData.generated.filter(f => !!f.signed_path).length;
      }

      return { signed, total };
    },
    totalDocsCount() {
      return this.signingCounts.total;
    },
    allUnsignedDocs() {
      const unsigned = [];

      if (this.finalStepData?.contract && !this.finalStepData.contract.signed_path) {
        unsigned.push({
          doc_key: this.finalStepData.contract?.doc_key,
          id: this.finalStepData.contract?.id,
          type: 'contract',
          typeId: this.finalStepData.contract?.type,
          raw: this.finalStepData.contract,
        });
      }

      if (Array.isArray(this.finalStepData?.user_docs)) {
        this.finalStepData.user_docs.forEach(doc => {
          if (!doc.signed_path) {
            unsigned.push({
              doc_key: doc.doc_key,
              id: doc.id,
              type: 'user_doc',
              typeId: doc.type,
              raw: doc
            });
          }
        });
      }

      if (Array.isArray(this.finalStepData?.generated)) {
        this.finalStepData.generated.forEach(doc => {
          if (!doc.signed_path) {
            unsigned.push({
              doc_key: doc.doc_key,
              id: doc.id,
              type: 'generated',
              typeId: doc.type,
              raw: doc
            });
          }
        });
      }

      return unsigned;
    },
    remainingSigningCount() {
      return this.signingCounts.total - this.signingCounts.signed;
    },
    signingMinutesLeft() {
      return Math.max(1, Math.ceil(this.remainingSigningCount * 15 / 60));
    },
    signingProgressPct() {
      if (!this.signingCounts.total) return 0;
      return Math.round((this.signingCounts.signed / this.signingCounts.total) * 100);
    },
  },
  mounted() {
    if (this.currentStep === 9) {
      this.loadListDocuments();
    }
    // if (this.currentStep === 5) {
    //   this.resumeProcessing();
    // }
  },
  watch: {
    currentStep(newVal) {
      if (newVal !== 8) {
        this.stopRedirectTimer();
      }
      if (newVal === 9) {
        this.loadListDocuments();
      }
    },
    allSigned: {
      handler(newVal) {
        if (newVal && !this.isOnboardingDone) {
          this.finishOnboarding();
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.stopRedirectTimer();
    Object.values(this.pollTimers).forEach(clearTimeout);
  },
  methods: {
    // searchCities(query) {
    // //  this.stateData.form.city_birth = query;
    //   //this.changedCity = true;
    //   if(query.length > 1){
    //     this.fetchCities(query,1);
    //   }
    // },
    searchCities(query) {
      if (!query || query.length < 2) return;
      this.fetchCities(query);
    },
    fetchCities(query) {
      axios.get('/api/directories/cities/find?city=' + query).then(({ data }) => {
        this.cities = data.data;
        this.citiesOptions = data.data;
        if (this.citiesOptions.length === 0) {
          this.citiesOptions.push(this.otherCity);
        }
      }).catch(err => (console.log(err)));
    },
    setCityBirth(option) {
      this.$set(this.stateData.form, 'city_birth', option ? option.name_full_name_ua : '');
      this.touchField('form.city_birth');
    },
    hasError(path) {
      if (!this.$v) return false;
      const keys = path.split('.');
      let obj = this.$v.stateData || this.$v;

      // for (const key of keys) {
      //   if (!obj || !(key in obj)) return false;
      //   obj = obj[key];
      // }
      for (const key of keys) {
        if (!obj) return false;

        if (obj.$each && !isNaN(key)) {
          obj = obj.$each[key];
        } else if (key in obj) {
          obj = obj[key];
        } else {
          return false;
        }
      }

      return obj ? obj.$error : false;
    },
    touchField(path) {
      if (!this.$v) return;
      const keys = path.split('.');
      let obj = this.$v.stateData;

      for (const key of keys) {
        if (!obj) return;

        if (obj.$each && !isNaN(key)) {
          obj = obj.$each[key];
        } else if (key in obj) {
          obj = obj[key];
        } else {
          return;
        }
      }

      if (obj && typeof obj.$touch === 'function') {
        obj.$touch();
      }
    },
    prevStep() {
      if (this.isGeneratingDocs) return;
      if (this.$v) this.$v.$reset();
      this.$emit('prev');
    },
    nextStep() {
      if (this.isGeneratingDocs) return;

      if (this.$v) {
        this.$v.$touch();
        if (this.$v.$invalid) {
          console.log('Помилка валидації:', this.getInvalidFields(this.$v));
          return;
        }
      }

      if (this.$v) this.$v.$reset();
      if (this.currentStep === 6) {
        this.setStepInsurersDone();
      }

      this.$emit('next');
    },
    selectStep(idx) {
      if (this.isGeneratingDocs) return;
      if (idx > this.currentStep && this.$v) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      }
      if (this.$v) this.$v.$reset();
      this.$emit('select-step', idx);
    },
    getInvalidFields(vObj, prefix = '') {
      let invalidKeys = [];
      for (const key in vObj) {
        if (key.startsWith('$')) continue;
        const field = vObj[key];
        const currentPath = prefix ? `${prefix}.${key}` : key;
        if (field && field.$invalid) {
          invalidKeys.push(currentPath);
          invalidKeys = invalidKeys.concat(this.getInvalidFields(field, currentPath));
        }
      }
      return invalidKeys;
    },
    formatInput(field, type, maxLength = null, nested = null) {
      let value = nested
        ? (this.stateData.form[nested]?.[field] || '')
        : (this.stateData.form[field] || '');

      switch (type) {
        case 'digits':
          value = value.replace(/\D/g, '');
          break;

        case 'iban':
          let digits = value.replace(/\D/g, '').slice(0, 27);
          value = 'UA' + digits;
          break;

        case 'ua_series':
          value = value.toUpperCase().replace(/[^А-ЯІЇЄҐ]/g, '');
          break;
      }

      if (type !== 'iban' && maxLength) {
        value = value.slice(0, maxLength);
      }

      if (nested) {
        if (!this.stateData.form[nested]) {
          this.stateData.form[nested] = {};
        }
        this.stateData.form[nested][field] = value;
      } else {
        this.stateData.form[field] = value;
      }
    },
    setStepInsurersDone() {
      if (this.activeInsurers.length > 0) {
        this.stateData.insurersDone = true;
      }
    },
    setHasSalesHead(val) {
      this.stateData.hasSalesHead = val;
      if (val === true && !this.stateData.form.sales_head) {
        this.stateData.form.sales_head = {
          name: '',
          rnokpp: '',
          birth: '',
          position: '',
          appointed: '',
          certificate: ''
        };
      }
    },
    setHasEmployees(val) {
      // this.stateData.hasEmployees = val;
      // if (val === true && (!this.stateData.form.employees || this.stateData.form.employees.length === 0)) {
      //   this.addEmployee();
      // }
      this.stateData.hasEmployees = val;
      if (val === true) {
        if (!Array.isArray(this.stateData.form.employees) || this.stateData.form.employees.length === 0) {
          this.$set(this.stateData.form, 'employees', [{
            name: '',
            rnokpp: '',
            birth: '',
            position: '',
            appointed: '',
            certificate: ''
          }]);
        }
      } else {
        this.$set(this.stateData.form, 'employees', []);
      }
    },
    addEmployee() {
      if (!Array.isArray(this.stateData.form.employees) || this.stateData.form.employees.length === 0) {
        this.$set(this.stateData.form, 'employees', []);
      }
      this.stateData.form.employees.push({
        name: '',
        rnokpp: '',
        birth: '',
        position: '',
        appointed: '',
        certificate: ''
      });
    },
    removeEmployee(index) {
      if (this.stateData.form.employees.length > 1) {
        this.stateData.form.employees.splice(index, 1);
      }
    },
    toggleInsurer(code) {
      if (this.stateData.insurersOff[code]) {
        this.$delete(this.stateData.insurersOff, code);
      } else {
        this.$set(this.stateData.insurersOff, code, true);
      }
      if (this.$v && this.$v.activeInsurers) {
        this.$v.activeInsurers.$touch();
      }
    },
    answerCompliance(key, val) {
      this.$set(this.stateData.compliance, key, val);
      if (this.$v && this.$v.stateData && this.$v.stateData.compliance && this.$v.stateData.compliance[key]) {
        this.$v.stateData.compliance[key].$touch();
      }
    },
    isComplianceTabDone(tabIndex) {
      return this.COMPLIANCE
        .filter(q => q.tab === tabIndex)
        .every(q => this.stateData.compliance[q.key]);
    },
    hasComplianceTabError(tabIndex) {
      if (!this.$v || !this.$v.stateData || !this.$v.stateData.compliance) return false;

      const tabQuestions = this.COMPLIANCE.filter(q => q.tab === tabIndex);

      return tabQuestions.some(q => {
        const fieldValidator = this.$v.stateData.compliance[q.key];
        return fieldValidator && fieldValidator.$error;
      });
    },
    getCurrentDataHash() {
      const d = this.stateData || {};

      return JSON.stringify({
        form: d.form,
        compliance: d.compliance,
        uploaded: d.uploaded,
        insurersOff: d.insurersOff,
        hasEmployees: d.hasEmployees,
        hasSalesHead: d.hasSalesHead,
        docType: d.docType,
        taxSystem: d.taxSystem,
        inRegistry: d.inRegistry,
        noRnokpp: d.noRnokpp,
        hasKved: d.hasKved
      });
    },
    startRedirectTimer() {
      this.stopRedirectTimer();
      this.redirectCountdown = 5;
      this.redirectTimer = setInterval(() => {
        this.redirectCountdown--;
        if (this.redirectCountdown <= 0) {
          this.stopRedirectTimer();
          this.nextStep();
        }
      }, 1000);
    },

    stopRedirectTimer() {
      if (this.redirectTimer) {
        clearInterval(this.redirectTimer);
        this.redirectTimer = null;
      }
    },

    goToNextStepNow() {
      this.stopRedirectTimer();
      this.nextStep();
    },
    uploadDoc(key) {
      this.$set(this.stateData.uploaded, key, true);
      this.stateData.points += 15;
      if (this.$v && this.$v.stateData && this.$v.stateData.uploaded && this.$v.stateData.uploaded[key]) {
        this.$v.stateData.uploaded[key].$touch();
      }
      // this.triggerToast('+15 XP — документ завантажено', '📁');
    },

    async resumeProcessing() {
      if (this.stateData.uploaded?.passport) return;
      try {
        const { data } = await checkOnboardingDocumentPagesStatus('passport');
        if (data.status === 'processing') {
          this.$set(this.processingDocs, 'passport', true);
          this.pollStatus('passport');
        } else if (data.status === 'ready') {
          this.$store.commit('SET_DOC_UPLOADED', { key: 'passport', data: data.data });
          await this.$store.dispatch('SAVE_ONBOARDING');
        }
      } catch (e) { }
    },

    openPagesModal(d) {
      this.pagesDoc = { key: d.key, label: d.label };
      this.showPagesModal = true;
    },

    onPagesConfirmed({ docKey }) {
      this.$set(this.processingDocs, docKey, true);
      // this.pollStatus(docKey);
      this.checkDocumentPagesPdfStatus(docKey);
    },

    async checkDocumentPagesPdfStatus(docKey) {

      try {

        const { data } = await checkOnboardingDocumentPagesStatus('passport');
        if (data.status === 'ready') {

          this.$store.commit('SET_DOC_UPLOADED', { key: docKey, data: data.data });

          await this.$store.dispatch('SAVE_ONBOARDING');
          this.$set(this.processingDocs, docKey, false);

          this.$message.success('Документ успішно оброблено');

        } else if (data.status === 'error') {

          this.$set(this.processingDocs, docKey, false);
          this.$message.error(data.error || 'Не вдалося сформувати PDF');

        } else {
         // this.pollStatus(docKey, attempt + 1);this.$set(this.processingDocs, docKey, false);
          this.$message.error(data.error || 'Не вдалося сформувати PDF');
        }

      } catch (e) {
        this.$message.error(data.error || 'Не вдалося сформувати PDF');
       // this.pollStatus(docKey, attempt + 1);
      }

    },

    pollStatus(docKey, attempt = 0) {
      if (attempt > 60) {
        this.$set(this.processingDocs, docKey, false);
        this.$message.error('Обробка триває занадто довго. Спробуйте ще раз.');
        return;
      }
      this.pollTimers[docKey] = setTimeout(async () => {
        try {
          const { data } = await checkOnboardingDocumentPagesStatus('passport');
          if (data.status === 'ready') {
            this.$store.commit('SET_DOC_UPLOADED', { key: docKey, data: data.data });
            await this.$store.dispatch('SAVE_ONBOARDING');
            this.$set(this.processingDocs, docKey, false);
            this.$message.success('Документ успішно оброблено');
          } else if (data.status === 'failed') {
            this.$set(this.processingDocs, docKey, false);
            this.$message.error(data.error || 'Не вдалося сформувати PDF');
          } else {
            this.pollStatus(docKey, attempt + 1);
          }
        } catch (e) {
          this.pollStatus(docKey, attempt + 1);
        }
      }, 2000);
    },

    isMultiPageDoc(key) {
      return this.MULTI_PAGE_DOCS.includes(key);
    },

    isPdfFile(file) {
      return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    },

    async handleFileUpload(fileOrFiles, docKey, rowData) {
      if (!fileOrFiles) return;

      let targetFile = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;

      if (targetFile && targetFile.raw) {
        targetFile = targetFile.raw;
      }

      if (!(targetFile instanceof File)) {
        console.error('Переданий объект не є файлом:', targetFile);
        return;
      }

      if (this.isMultiPageDoc(docKey) && !this.isPdfFile(targetFile)) {
        this.$message.warning('У режимі PDF можна завантажити лише PDF. Для фото оберіть режим «Фото сторінок».');
        return;
      }

      this.$set(this.loadingDocs, docKey, true);

      try {
        const formData = new FormData();
        formData.append('file', targetFile);
        formData.append('key', docKey);
        formData.append('name_ua', rowData.label ?? null);

        const response = await axios.post('/api/onboarding/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const resData = response.data.data || response.data;

        this.$store.commit('SET_DOC_UPLOADED', {
          key: docKey,
          data: {
            id: resData.id,
            key: resData.key,
            name_ua: resData.name_ua,
            path: resData.path,
            url: resData.url
          }
        });

        await this.$store.dispatch('SAVE_ONBOARDING');

        this.$message.success(`Файл "${targetFile.name}" успішно завантажено`);

        if (typeof this.clearError === 'function') {
          this.clearError('uploaded.' + docKey);
        }

        if (this.clearError) {
          this.clearError('uploaded.' + docKey);
        }

      } catch (error) {
        console.error('Помилка завантаження файла:', error);
       // this.$message.error('Не вдалося завантажити файл. Спробуйте ще раз.');
        this.$message.error(error.response?.data?.message || 'Не вдалося завантажити файл. Спробуйте ще раз.');
      } finally {
        this.$set(this.loadingDocs, docKey, false);
      }
    },

    async handleFileRemove(docKey) {
      const fileData = this.stateData?.uploaded?.[docKey];
      this.$set(this.loadingDocs, docKey, true);

      try {
        const fileId = typeof fileData === 'object' ? fileData.id : null;

        if (fileId) {
          await axios.delete(`/api/onboarding/documents/${fileId}`);
        } else {
          await axios.delete(`/api/onboarding/documents/type/${docKey}`);
        }

        this.$store.commit('REMOVE_DOC_UPLOADED', docKey);

        await this.$store.dispatch('SAVE_ONBOARDING');

        this.$message.info('Файл видалено');
      } catch (error) {
        console.error('Помилка видалення файла:', error);
        this.$message.error('Не вдалося видалити файл');
      } finally {
        this.$set(this.loadingDocs, docKey, false);
      }
    },

    getFileName(docKey) {
      const file = this.stateData.uploaded[docKey];
      if (!file) return '';
      if (typeof file === 'string') return file;
      return file.name || 'Завантажений документ';
    },

    async loadListDocuments() {
      this.loadingStep9 = true;
      try {
        const response = await getListDocuments();
        if (response.data.status === 'success') {
          this.finalStepData = response.data.data;
        }
      } catch (e) {
        this.$message?.error('Не вдалося завантажити список документов');
      } finally {
        this.loadingStep9 = false;
      }
    },

    toggleGroup(key) {
      this.stateData.openGroup = this.stateData.openGroup === key ? null : key;
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedDocIds = this.allUnsignedDocs.map(doc => doc.id);
      } else {
        this.selectedDocIds = [];
        this.confirmSignAll = false;
      }
    },

    isDocSelected(docId) {
      return this.selectedDocIds.includes(docId);
    },

    isDocSelectedAlt(docId) {
      return this.allUnsignedDocs.find(item => item.doc);
    },

    toggleDocSelection(docId) {
      const index = this.selectedDocIds.indexOf(docId);
      if (index === -1) {
        this.selectedDocIds.push(docId);
      } else {
        this.selectedDocIds.splice(index, 1);
      }

      this.selectAll = this.selectedDocIds.length === this.allUnsignedDocs.length && this.allUnsignedDocs.length > 0;
      if (!this.selectAll) {
        this.confirmSignAll = false;
      }
    },

    openSignModal(doc) {
      if (!doc || !doc.id) return;
      this.targetDocToSign = doc;
      this.signingMode = 'single';
      this.showKepModal = true;
    },

    openBatchSignModal() {
      if (!this.confirmSignAll || this.selectedDocIds.length === 0) return;
      this.targetDocToSign = null;
      this.signingMode = 'batch';
      this.showKepModal = true;
    },

    async signAction(doc, credentials = null) {
      if (!doc || !doc.id) return;

      if (!credentials) {
        this.targetDocToSign = doc;
        this.signingMode = 'single';
        this.showKepModal = true;
        return;
      }

      this.isSigningProgress = true;
      const formData = new FormData();
      formData.append('document_id', doc.id);
      formData.append('key_file', credentials.file);
      formData.append('key_password', credentials.password);
      formData.append('ca_id', credentials.caCode);

      try {
        const response = await signDocument(formData);

        if (response.data?.status === 'success' || response.data?.success) {
          this.$message?.success('Документ успішно підписано КЕП');
          this.$toast?.success('Документ успішно підписано КЕП');

          if (response.data?.data?.signed_path) {
            doc.signed_path = response.data.data.signed_path;
          }
          if (this.stateData?.signedDocs) {
            this.$set(this.stateData.signedDocs, doc.id, true);
            //this.$message?.success('Документ успішно підписано КЕП');
          }

          this.showKepModal = false;
        }

      } catch (error) {
        console.error('Помилка підписання КЕП:', error);
        const errorMsg = error.response?.data?.message || 'Не вдалося підписати документ КЕП.';

        this.showKepModal = false;
        // this.$toast?.error(errorMsg, { timeout: 10000 });
        // this.$message?.error(errorMsg, { timeout: 10000 });

        this.$message.error({
          dangerouslyUseHTMLString: true,
          message: errorMsg,
          duration: 8000,
          showClose: true
        });

      } finally {
        this.isSigningProgress = false;
      }

    },

    async signAll(credentials = null) {
      if (!this.confirmSignAll || this.selectedDocIds.length === 0) return;

      if (!credentials) {
        this.targetDocToSign = null;
        this.signingMode = 'batch';
        this.showKepModal = true;
        return;
      }

      this.isSigningProgress = true;
      const formData = new FormData();
      formData.append('key_file', credentials.file);
      formData.append('key_password', credentials.password);

      this.selectedDocIds.forEach((id) => {
        formData.append('documentIds[]', id);
      });

      try {
        const response = await signAllDocuments(formData);

        if (response.data?.status === 'success' || response.data?.success) {
          this.$toast?.success('Документи успішно підписано КЕП!');
          this.$message?.success('Документи успішно підписано КЕП!');

          this.selectAll = false;
          this.confirmSignAll = false;
          this.selectedDocIds = [];

          //this.$toast?.success('Документи успішно підписано!');

          if (typeof this.loadListDocuments === 'function') {
            await this.loadListDocuments();
          }

          this.showKepModal = false;
        }
      } catch (error) {
        console.error('Помилка пакетного підписання КЕП:', error);
        const errorMsg = error.response?.data?.message || 'Не вдалося підписати документи КЕП.';
        this.$toast?.error(errorMsg);
        this.$message?.error(errorMsg);
      } finally {
        this.isSigningProgress = false;
      }
    },

    /**
     * Обработчик отправки формы из KepSignModal
     * @param {Object} payload - { file, password }
     */
    onKepModalSubmit(payload) {
      if (this.signingMode === 'single') {
        this.signAction(this.targetDocToSign, payload);
      } else if (this.signingMode === 'batch') {
        this.signAll(payload);
      }
    },

    async finishOnboarding() {
      if (this.isCompleting || this.isOnboardingDone) return;

      this.isCompleting = true;

      try {

        const response = completeOnboarding();

        this.isOnboardingDone = true;
        this.$toast?.success('Вітаємо! Онбординг успішно завершено.');

        console.log('Онбординг завершено успішно:', response.data);

      } catch (error) {
        console.error('Помилка при завершенні онбордингу:', error);
        this.$toast?.error('Не вдалося завершити онбординг. Спробуйте оновити сторінку.');
      } finally {
        this.isCompleting = false;
      }
    },

    async renderDocuments() {
      if (this.isGeneratingDocs) return;
      this.stopRedirectTimer();

      this.$emit('update-generating', true);

      try {
        const response = await generateDocuments({step: 1});
        const generatedDocsCount = response.data.files?.length || 0;

        this.$store.commit('SET_TOTAL_GENERATED_DOCS_COUNT', generatedDocsCount);

        this.$set(this.stateData, 'docsGenerated', true);
        this.$set(this.stateData, 'lastGeneratedHash', this.getCurrentDataHash());

        await this.$store.dispatch('SAVE_ONBOARDING');

        if (this.$message) {
          this.$message.success('Пакет документів успішно сформовано!');
        }

        this.startRedirectTimer();

      } catch (error) {
        console.error('Помилка формування документів:', error);
        this.errors = error.response?.data?.data || error.message;
        if (this.$message) {
          this.$message.error('Помилка при формуванні документів. Спробуйте ще раз.');
        }
      } finally {
        this.$emit('update-generating', false);
      }

    },

    async viewContract() {
      // window.open('/api/onboarding/contract-template?download=0', '_blank');
      // window.location.assign(response.data.data.contract);
      const newTab = window.open('about:blank', '_blank');

      try {

        const response = await showExampleDocument();
        const file = new Blob([response.data], { type: 'application/pdf' });
        const fileUrl = URL.createObjectURL(file);

        if (newTab) {
          newTab.location.href = fileUrl;
        }
      } catch (error) {

        if (newTab) newTab.close();

        console.error('Помилка перегляду договору:', error);
        if (this.$message) {
          this.$message.error('Не вдалося відкрити файл договору.');
        }
      }
    },

    async downloadContract() {
      if (this.isDownloadingContract) return;
      this.isDownloadingContract = true;

      try {
        const response = await axios.get('/api/onboarding/contract-template?download=1', {
          responseType: 'blob',
        });

        if (response?.data) {
          FileDownload(response.data, 'Subagent-contract.pdf');
        }
      } catch (error) {
        console.error('Помилка завантаження договору:', error);
        if (this.$message) {
          this.$message.error('Не вдалося завантажити файл договору.');
        }
      } finally {
        this.isDownloadingContract = false;
      }
    },

    async downloadTemplate(template) {
      try {
        const response = await axios.get(`/api/onboarding/template?path=${encodeURIComponent(template.path)}&download=1`, {
          responseType: 'blob'
        });
        FileDownload(response.data, template.label);
      } catch (e) {
        this.$message?.error('Не вдалося завантажити файл');
      }
    },

    async viewFileByDocId(docId, isSigned = false) {
      const url = `/api/onboarding/document/file?id=${docId}&signed=${isSigned ? 1 : 0}`;
      await this.viewFile(url, docId, isSigned);
    },

    async downloadFileByDocId(docId, filename, isSigned = false) {
      const url = `/api/onboarding/document/file?id=${docId}&signed=${isSigned ? 1 : 0}`;
      await this.downloadFile(docId, isSigned, filename);
    },

    async downloadSignedForm(template) {
      try {
        const response = await axios.get(`/api/onboarding/signed-file?path=${encodeURIComponent(template.path)}&download=1`, {
          responseType: 'blob'
        });
        FileDownload(response.data, `signed_${template.label}`);
      } catch (e) {
        this.$message?.error('Не вдалося завантажити підписаний файл');
      }
    },

    async viewFile(endpointUrl, docId, isSigned) {
      const isDownload = 0;
      const userId = this.userInfo.id;

      // const newTab = window.open('about:blank', '_blank');
      const token = localStorage.getItem('token') || '';
      const directUrl = `/api/onboarding/documents/show/${userId}?id=${docId}&signed=${isSigned ? 1 : 0}&isDownload=0&bearer_token=${token}`;
      window.open(directUrl, '_blank');
      // try {
      //
      //   const response = await showDocumentFile(userId ,docId, isSigned, isDownload); // axios.get(fullUrl, { responseType: 'blob' });
      //   const contentType = response.headers['content-type'] || 'application/pdf';
      //   const fileBlob = new Blob([response.data], { type: contentType });
      //   const blobUrl = URL.createObjectURL(fileBlob);
      //
      //   if (newTab) {
      //     newTab.location.href = blobUrl;
      //   }
      // } catch (error) {
      //   if (newTab) newTab.close();
      //   if (this.$message) {
      //     this.$message.error('Не вдалося відкрити файл');
      //   }
      // }
    },

    async downloadFile(docId, isSigned, filename = 'document', endpointUrl) {
      const isDownload = 1;

      try {

        const userId = this.userInfo.id;
        const response = await showDocumentFile(userId, docId, isSigned, isDownload);//axios.get(fullUrl, { responseType: 'blob' });

        const contentType = response.headers['content-type'] || 'application/pdf';
        const fileBlob = new Blob([response.data], { type: contentType });
        const blobUrl = URL.createObjectURL(fileBlob);

        let ext = 'pdf';
        if (contentType.includes('image/jpeg')) ext = 'jpg';
        if (contentType.includes('image/png')) ext = 'png';

        let finalFilename = filename;
        if (!finalFilename.toLowerCase().endsWith(`.${ext}`)) {
          finalFilename = `${finalFilename}.${ext}`;
        }

        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', finalFilename);
        document.body.appendChild(link);
        link.click();

        link.remove();
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Помилка завантаження файлу:', error);
        if (this.$message) {
          this.$message.error('Не вдалося завантажити файл');
        }
      }
    }

  }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.has-error .input {
  border-color: #ef4444 !important;
  background-color: #fef2f2 !important;
}

.has-error .card {
  border: 1px solid #ef4444 !important;
}

.chip-group-error {
  outline: 1px solid #ef4444;
  border-radius: 8px;
  padding: 2px;
}

.field-error {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
  display: block;
  font-weight: 500;
}

.req {
  color: #ef4444;
}

/* Основной контейнер карточки */
.doc {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  background-color: #fff;
  border-radius: 12px;
  border: 1px solid #eaedf0;
}

/* Левый блок с текстом */
.doc-body {
  flex: 1;
  min-width: 0; /* Предотвращает переполнение flex-контейнера длинным текстом */
}


.doc-label {
  font-weight: 600;
  color: #1a1d20;
  font-size: 15px;
}

.doc-hint {
  font-size: 13px;
  color: #8c92a0;
  margin-top: 4px;
}

.doc-mode {
  margin-top: 10px;
}

/* Правый блок с элементами управления */
.doc-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0; /* Не дает кнопкам сжиматься */
}

.doc-upload-btn {
  width: 140px; /* Стабильная ширина для всех состояний */
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

/* Переопределение 100% ширины внутри компонента FileInput */
.doc-upload-btn >>> .upload-file,
.doc-upload-btn >>> .el-upload,
.doc-upload-btn >>> .w-100 {
  width: auto !important;
  display: inline-block !important;
}



/* Стилизация оранжевой кнопки как на верхнем скриншоте */
.doc-upload-btn >>> .el-button {
  border: 1px solid #f5923e !important;
  color: #f5923e !important;
  background-color: #fff !important;
  font-weight: 600;
  border-radius: 8px;
  padding: 8px 18px;
  transition: all 0.2s ease;
}

.doc-upload-btn >>> .el-button:hover {
  background-color: #fff8f3 !important;
  border-color: #e07d2a !important;
  color: #e07d2a !important;
}

/* Блок загруженного файла */
/*.uploaded-file-info {*/
/*  display: flex;*/
/*  align-items: center;*/
/*  gap: 8px;*/
/*  background: #f4f6f8;*/
/*  padding: 6px 12px;*/
/*  border-radius: 8px;*/
/*  font-size: 13px;*/
/*}*/
.uploaded-file-info {
  width: 100%;
  max-width: 140px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 6px 8px;
  background-color: #f4f6f8;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-sizing: border-box;
}


.uploaded-file-info .file-name {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uploaded-file-info .btn-delete {
  color: #f56c6c;
  padding: 0 4px;
  font-weight: bold;
  border-radius: 40px;
}

/* ==========================================
   Мобільна версія (≤768px)
   ========================================== */
@media (max-width: 768px) {
  .doc {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 14px;
  }
  .doc-label { font-size: 14px; line-height: 1.4; }
  .doc-hint {
    font-size: 12px;
    line-height: 1.45;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
  .doc-actions {
    width: 100%;
    justify-content: space-between;
    flex-shrink: 1;
    min-width: 0;
  }
  .doc-upload-btn {
    width: auto;
    flex: 1;
    min-width: 0;
    justify-content: flex-end;
  }
  .doc-upload-btn >>> .el-button {
    min-height: 44px;
    padding: 10px 18px;
    font-size: 14px;
  }
  .uploaded-file-info {
    min-width: 0;
    max-width: none;
    padding: 4px 4px 4px 10px;
  }
  .uploaded-file-info .file-name {
    max-width: none;
    flex: 1;
    min-width: 0;
  }
  .uploaded-file-info .btn-delete {
    width: 26px;
    height: 26px;
    padding: 2px 5px;
    border-radius: 30px;
    min-height: 20px;
  }
  .field-error { font-size: 12.5px; }
}
</style>
